import { apiBasePath } from '../baseUrl';
import { apiUrl } from '../../utils/api';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '../../constants/pagination';

export const extendedApi = apiBasePath.injectEndpoints({
  endpoints: (build) => ({
    addLease: build.mutation({
      query: (data) => ({
        url: `${apiUrl.lease}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Leases'],
    }),
    getLeases: build.query({
      query: (args) => {
        if (args) {
          const { page, perPage, searchParams = '' } = args;
          return `${apiUrl.lease}?perPage=${perPage || DEFAULT_PER_PAGE}&page=${page || DEFAULT_PAGE}&${searchParams}`;
        }
        return `${apiUrl.lease}`;
      },
      transformResponse: (responseData) => {
        const contractData = responseData?.data?.contracts;
        const data = contractData?.map((lease) => {
          const productTypes =
            lease?.contractSublocations?.flatMap((subloc) => {
              const productCategoriesNames = subloc?.sublocation?.productCategories?.map(
                (productCategory) => productCategory?.name,
              );
              return productCategoriesNames;
            }) ?? [];
          return { ...lease, productTypes };
        });
        return {
          data,
          pagination: responseData?.pagination,
        };
      },
      providesTags: (result) =>
        result ? ['Leases', ...result?.data?.map(({ id }) => ({ type: 'Leases', id }))] : ['Leases'],
    }),
    getLeaseById: build.query({
      query: (leaseId) => `${apiUrl.lease}/${leaseId}`,
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [{ type: 'Leases', id: arg }],
    }),
    getLeaseByIdWithVersion: build.query({
      query: ({ leaseId, version }) => `${apiUrl.lease}/${leaseId}?version=${version}`,
      transformResponse: (responseData) => responseData?.data,
      providesTags: (result, error, arg) => [{ type: 'Leases', id: arg.leaseId }],
    }),
    getLeaseHistoryById: build.query({
      query: ({ leaseId, page, perPage }) => `${apiUrl.lease}/history/${leaseId}?page=${page}&perPage=${perPage}`,
      transformResponse: (responseData) => ({
        data: responseData?.data,
        pagination: responseData?.pagination,
      }),
      providesTags: (result, error, arg) => [{ type: 'Leases', id: arg.leaseId }],
    }),
    updateLeaseById: build.mutation({
      query: ({ id, ...rest }) => ({
        url: `${apiUrl.lease}/${id}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Leases', id: arg.id }, 'Sublocation', 'SubLocations'],
    }),
    deleteLeaseHistory: build.mutation({
      query: ({ leaseId, version, updatedBy }) => ({
        url: `${apiUrl.lease}/${apiUrl.history}/${leaseId}${version ? `?version=${version}` : ''}`,
        method: 'DELETE',
        body: updatedBy ? { updatedBy } : undefined,
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Leases', id: arg.leaseId }],
    }),
    validateSubLocations: build.query({
      query: ({ locationId, subLocationIds, termStartDate, termEndDate, contractId }) =>
        `${apiUrl.lease}/${
          apiUrl.validateSubLocationsPeriod
        }?locationId=${locationId}&sublocationIds=${subLocationIds}&termStartDate=${termStartDate}&termEndDate=${termEndDate}${
          contractId ? `&contractId=${contractId}` : ''
        }`,
      transformResponse: (responseData) => responseData?.data,
    }),
    getSalesAssociates: build.query({
      query: () => `${apiUrl.salesAssociate}/`,
    }),
    getRentOptions: build.query({
      query: () => `${apiUrl.lease}/${apiUrl.rentOptions}`,
      transformResponse: (responseData) => responseData?.data?.rentOptions ?? [],
    }),
    overlapLease: build.mutation({
      query: (data) => ({
        url: `${apiUrl.lease}/overried-contract`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Leases'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddLeaseMutation,
  useGetLeasesQuery,
  useGetLeaseByIdQuery,
  useGetLeaseHistoryByIdQuery,
  useUpdateLeaseByIdMutation,
  useDeleteLeaseHistoryMutation,
  useLazyValidateSubLocationsQuery,
  useOverlapLeaseMutation,
  useGetSalesAssociatesQuery,
  useGetLeaseByIdWithVersionQuery,
  useGetRentOptionsQuery,
} = extendedApi;
